import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center justify-center rounded-full border px-2 py-0.5 text-xs font-medium w-fit whitespace-nowrap shrink-0 [&>svg]:size-4 gap-1 [&>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground [a&]:hover:bg-primary/90',
        secondary: 'border bg-secondary text-gray-950 [a&]:hover:bg-secondary/90',
        destructive:
          'border-transparent bg-red-bg border-red-border text-red-foreground  [a&]:hover:bg-red-100',
        success:
          'border-transparent bg-green-bg border-green-border text-green-foreground  [a&]:hover:bg-green-100',
        warning:
          'border-transparent bg-yellow-bg border-yellow-border text-yellow-foreground  [a&]:hover:bg-yellow-100',
        info: 'border-transparent bg-blue-bg border-blue-border text-blue-foreground [&>svg]:animate-spin  [a&]:hover:bg-blue-100',
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return <Comp data-slot='badge' className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
